import React, { createContext, ReactNode, useContext, useState } from "react";
import { IClient } from "interfaces/Client";
import { IFormError } from "interfaces/ExpressError";
import { IProduct } from "interfaces/Product";
import { IService } from "interfaces/Service";
import { IUser } from "interfaces/User";
import { getClientService } from "services/ClientService";

type ProviderValues = {
  children: ReactNode;
};

type ContextValues = {
  // user: IUser;
  client: IClient;
  product: IProduct;
  errors: IFormError;
  searching: boolean;
  selectedServices: IService[];
  regions: [string, number][];
  productRegions: ProductRegion[];
  hasChanges: boolean;
  setClient: React.Dispatch<React.SetStateAction<IClient>>;
  setProduct: React.Dispatch<React.SetStateAction<IProduct>>;
  setProductRegions: React.Dispatch<React.SetStateAction<ProductRegion[]>>;
  setErrors: React.Dispatch<React.SetStateAction<IFormError>>;
  // setUser: React.Dispatch<React.SetStateAction<IUser>>;
  setSelectedServices: React.Dispatch<React.SetStateAction<IService[]>>;
  setRegions: React.Dispatch<React.SetStateAction<[string, number][]>>;
  isServiceSelected: (service: IService) => boolean;
  removeSelectedService: (service: IService) => void;
  removeProductRegion: (region: ProductRegion, index: number) => void;
  removeRegion: (region: [string, number]) => void;
  removeError: (field: string) => void;
  addError: (field: string, message: string) => void;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  resetClient: () => void;
  storeClient: (client: IClient) => void;
};

export type ProductRegion = {
  readonly id?: string;
  name: string;
  regionValue: number;
  kmValue: number;
  hourValue: number;
};

export const ClientRegisterContext = createContext({} as ContextValues);

export function ClientRegisterProvider({ children }: ProviderValues) {
  // const [user, setUser] = useState<IUser>({} as IUser);
  const [client, setClient] = useState<IClient>({} as IClient);
  const [product, setProduct] = useState<IProduct>({} as IProduct);

  const [errors, setErrors] = useState<IFormError>({});

  const [selectedServices, setSelectedServices] = useState<IService[]>([]);

  const [searching, setSearching] = useState<boolean>(false);

  const [regions, setRegions] = useState<[string, number][]>([]);
  const [productRegions, setProductRegions] = useState<ProductRegion[]>([]);

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  async function storeClient(clientData: IClient) {
    setSearching(true);
    return await getClientService(clientData.id)
      .then((res) => {
        const client = res.item;

        const _regions = client.product?.stringRegions;
        if (_regions) setRegions(JSON.parse(_regions));

        const _productRegions = client.product?.productRegions;
        if (_productRegions) setProductRegions(_productRegions);

        const services =
          client.product?.services !== undefined ? client.product.services : [];
        setSelectedServices(services);

        setClient(client);
        if (client.product) setProduct(client.product);
        // if (client.user) setUser(client.user);

        setSearching(false);
      })
      .catch((err) => {
        console.log(err);
        setSearching(false);
      });
  }

  function isServiceSelected(service: IService) {
    return selectedServices.includes(service);
  }

  function removeSelectedService(service: IService) {
    const serviceList = [...selectedServices];
    const filteredList = serviceList.filter(
      (element) => element.id !== service.id
    );

    setSelectedServices(filteredList);
  }

  function removeRegion(region: [string, number]) {
    const regionList = [...regions];
    const regionIndex = regionList.indexOf(region);

    if (regionIndex === -1) return;

    regionList.splice(regionIndex, 1);

    setRegions(regionList);
  }

  function removeProductRegion(region: ProductRegion, index: number) {
    const regionList = [...productRegions];
    regionList.splice(index, 1);

    setProductRegions(regionList);
  }

  function addError(field: string, message: string) {
    const _errors = { ...errors };
    _errors[field] = message;

    setErrors(() => {
      return _errors;
    });
  }

  function removeError(field: string) {
    const _errors = { ...errors };
    delete _errors[field];

    setErrors(() => {
      return _errors;
    });
  }

  function resetClient() {
    setSearching(true);
    // setUser({} as IUser);
    setClient({} as IClient);
    setProduct({} as IProduct);
    setErrors({});
    setSelectedServices([]);
    setRegions([]);
    setSearching(false);
  }

  return (
    <ClientRegisterContext.Provider
      value={{
        client,
        setClient,
        product,
        setProduct,
        errors,
        setErrors,
        searching,
        isServiceSelected,
        removeSelectedService,
        selectedServices,
        setSelectedServices,
        regions,
        productRegions,
        setProductRegions,
        setRegions,
        removeProductRegion,
        removeRegion,
        // user,
        // setUser,
        addError,
        removeError,
        hasChanges,
        setHasChanges,
        resetClient,
        storeClient,
      }}
    >
      {children}
    </ClientRegisterContext.Provider>
  );
}

export const useClientRegister = () => {
  return useContext(ClientRegisterContext);
};
